import {useEffect, useRef} from 'react'

/**
 * Mouse click-and-drag for a horizontally-scrolling container.
 *
 * Touch already scrolls natively via `overflow-x:auto`; this hook adds the same
 * affordance for desktop pointers by translating mousedown→mousemove→mouseup
 * into direct `scrollLeft` updates. Native scroll-snap re-engages on release
 * and snaps to the nearest slide.
 *
 * Click-after-drag is suppressed via a capture-phase click handler so a drag
 * that ends on a child link/button does not also fire that link/button.
 *
 * @param {React.RefObject<HTMLElement>} ref - Container with `overflow-x:auto`.
 * @param {{enabled: boolean, refreshKey: unknown}} options
 */
export const useDragScroll = (ref, {enabled = true, refreshKey} = {}) => {
    const stateRef = useRef({
        active: false,
        startX: 0,
        startLeft: 0,
        moved: false,
        pointerId: null
    })

    useEffect(() => {
        if (!enabled) return undefined
        const node = ref.current
        if (!node) return undefined

        /* Pointer capture is DEFERRED until movement exceeds the drag threshold
         * (see onPointerMove). Capturing on pointerdown would redirect the
         * subsequent click event to the container on some browsers, eating
         * clicks on descendant buttons (e.g. QuickView, wishlist). By
         * capturing only after a real drag is in progress, pure clicks pass
         * through to the original target unchanged. */
        const onPointerDown = (e) => {
            if (e.pointerType === 'touch') return
            if (e.button !== 0) return
            stateRef.current = {
                active: true,
                startX: e.clientX,
                startLeft: node.scrollLeft,
                moved: false,
                pointerId: e.pointerId
            }
        }

        const onPointerMove = (e) => {
            const s = stateRef.current
            if (!s.active) return
            const dx = e.clientX - s.startX
            if (!s.moved) {
                if (Math.abs(dx) <= 4) return
                /* Threshold crossed — promote to a drag. Capture pointer so
                 * tracking continues even if the pointer leaves the container,
                 * and apply visual cues. */
                s.moved = true
                try {
                    node.setPointerCapture(s.pointerId)
                } catch {
                    /* setPointerCapture can throw on some browsers if the pointer is gone. */
                }
                node.style.cursor = 'grabbing'
                /* Disable snap during drag so scrollLeft tracks the cursor smoothly;
                 * restored on release so the browser snaps to the nearest slide. */
                node.style.scrollSnapType = 'none'
                node.style.userSelect = 'none'
            }
            node.scrollLeft = s.startLeft - dx
        }

        const release = () => {
            const s = stateRef.current
            if (!s.active) return
            s.active = false
            if (s.moved) {
                node.style.cursor = ''
                node.style.scrollSnapType = ''
                node.style.userSelect = ''
            }
        }

        const onClickCapture = (e) => {
            /* If the previous pointer sequence moved beyond the threshold, eat the
             * click so a drag-and-release on top of a child link doesn't navigate. */
            if (stateRef.current.moved) {
                e.stopPropagation()
                e.preventDefault()
                stateRef.current.moved = false
            }
        }

        /* Suppress the browser's native HTML5 drag (which would otherwise hijack
         * mousedown-on-image and show a ghost preview, preventing our scroll
         * drag from working over `<img>` and `<a>` children). */
        const onDragStart = (e) => {
            e.preventDefault()
        }

        node.addEventListener('pointerdown', onPointerDown)
        node.addEventListener('pointermove', onPointerMove)
        node.addEventListener('pointerup', release)
        node.addEventListener('pointercancel', release)
        node.addEventListener('click', onClickCapture, true)
        node.addEventListener('dragstart', onDragStart)

        return () => {
            node.removeEventListener('pointerdown', onPointerDown)
            node.removeEventListener('pointermove', onPointerMove)
            node.removeEventListener('pointerup', release)
            node.removeEventListener('pointercancel', release)
            node.removeEventListener('click', onClickCapture, true)
            node.removeEventListener('dragstart', onDragStart)
            node.style.cursor = ''
            node.style.scrollSnapType = ''
            node.style.userSelect = ''
        }
    }, [enabled, ref, refreshKey])
}
